import {t} from 'i18next';
import {
  EscalationTrigger,
  ExclusionGate,
  NegativeExclusionQuestion,
  PrimaryDecisionQuestion,
  RelevanceCriteria,
  SecondarySupportingQuestion,
} from '../../../../../redux/slice/aiReviewSliceV2';
import {
  QuestionReadOnlyListItem,
  QuestionReadOnlyListSection,
} from '../question-read-only-list-v2/QuestionReadOnlyList';

const SECTION_KEY_PREFIX = 'ai.review.protocolWizard.relevanceCriteria';

const hasDisplayText = (...values: Array<string | undefined>) =>
  values.some((value) => Boolean(value?.trim()));

const toOptionalText = (value: string): string | undefined =>
  value.trim() ? value : undefined;

const formatLabeledDescription = (label: string, value: string) =>
  value.trim() ? `${label}: ${value}` : '';

const mapExclusionGates = (
  gates: ExclusionGate[]
): QuestionReadOnlyListItem[] =>
  gates
    .filter((gate) =>
      hasDisplayText(gate.id, gate.other, gate.hard_rule, gate.tag)
    )
    .map((gate, index) => ({
      key: `exclusion-gate-${gate.id || index}`,
      id: toOptionalText(gate.id),
      title: toOptionalText(gate.other),
      description: gate.hard_rule,
      tag: toOptionalText(gate.tag),
    }));

const mapQuestions = (
  items: Array<
    | PrimaryDecisionQuestion
    | SecondarySupportingQuestion
    | NegativeExclusionQuestion
    | EscalationTrigger
  >,
  keyPrefix: string,
  descriptionLabel: string
): QuestionReadOnlyListItem[] =>
  items
    .filter((item) =>
      hasDisplayText(
        item.id,
        item.question_text,
        'rationale' in item ? item.rationale : item.purpose
      )
    )
    .map((item, index) => ({
      key: `${keyPrefix}-${item.id || index}`,
      id: toOptionalText(item.id),
      title: toOptionalText(item.question_text),
      description: formatLabeledDescription(
        descriptionLabel,
        'rationale' in item ? item.rationale : item.purpose
      ),
    }));

export const mapRelevanceCriteriaToReadOnlySections = (
  criteria: RelevanceCriteria
): QuestionReadOnlyListSection[] => [
  {
    id: 'master-relevance-standard',
    label: t(`${SECTION_KEY_PREFIX}.sections.masterStandard`),
    tooltipContent: t(`${SECTION_KEY_PREFIX}.tooltips.masterStandard`),
    defaultExpanded: true,
    items: criteria.master_relevance_standard.trim()
      ? [
          {
            key: 'master-relevance-standard-item',
            description: criteria.master_relevance_standard,
          },
        ]
      : [],
  },
  {
    id: 'exclusion-gates-hard-rules',
    label: t(`${SECTION_KEY_PREFIX}.sections.exclusionGates`),
    tooltipContent: t(`${SECTION_KEY_PREFIX}.tooltips.exclusionGates`),
    defaultExpanded: true,
    items: mapExclusionGates(criteria.exclusion_gates),
  },
  {
    id: 'primary-decision-questions',
    label: t(`${SECTION_KEY_PREFIX}.sections.primaryDecisionQuestions`),
    tooltipContent: t(
      `${SECTION_KEY_PREFIX}.tooltips.primaryDecisionQuestions`
    ),
    defaultExpanded: true,
    items: mapQuestions(
      criteria.primary_decision_questions,
      'primary-decision-question',
      t(`${SECTION_KEY_PREFIX}.fields.rationale`)
    ),
  },
  {
    id: 'secondary-supporting-questions',
    label: t(`${SECTION_KEY_PREFIX}.sections.secondarySupportingQuestions`),
    tooltipContent: t(
      `${SECTION_KEY_PREFIX}.tooltips.secondarySupportingQuestions`
    ),
    defaultExpanded: true,
    items: mapQuestions(
      criteria.secondary_supporting_questions,
      'secondary-supporting-question',
      t(`${SECTION_KEY_PREFIX}.fields.purpose`)
    ),
  },
  {
    id: 'negative-exclusion-questions',
    label: t(`${SECTION_KEY_PREFIX}.sections.negativeExclusionQuestions`),
    tooltipContent: t(
      `${SECTION_KEY_PREFIX}.tooltips.negativeExclusionQuestions`
    ),
    defaultExpanded: true,
    items: mapQuestions(
      criteria.negative_exclusion_questions,
      'negative-exclusion-question',
      t(`${SECTION_KEY_PREFIX}.fields.purpose`)
    ),
  },
  {
    id: 'escalating-triggers',
    label: t(`${SECTION_KEY_PREFIX}.sections.escalationTriggers`),
    tooltipContent: t(`${SECTION_KEY_PREFIX}.tooltips.escalationTriggers`),
    defaultExpanded: true,
    items: mapQuestions(
      criteria.escalation_triggers,
      'escalating-trigger',
      t(`${SECTION_KEY_PREFIX}.fields.escalationReason`)
    ),
  },
];

export const createEmptyExclusionGate = (): ExclusionGate => ({
  id: '',
  other: '',
  hard_rule: '',
  tag: '',
});

export const createEmptyPrimaryDecisionQuestion =
  (): PrimaryDecisionQuestion => ({
    id: '',
    question_text: '',
    rationale: '',
  });

export const createEmptySecondarySupportingQuestion =
  (): SecondarySupportingQuestion => ({
    id: '',
    question_text: '',
    purpose: '',
  });

export const createEmptyNegativeExclusionQuestion =
  (): NegativeExclusionQuestion => ({
    id: '',
    question_text: '',
    purpose: '',
  });

export const createEmptyEscalationTrigger = (): EscalationTrigger => ({
  id: '',
  question_text: '',
  purpose: '',
});

export const updateItem = <T>(items: T[], index: number, value: T): T[] =>
  items.map((item, itemIndex) => (itemIndex === index ? value : item));

export const appendItem = <T>(items: T[], value: T): T[] => [...items, value];

export const removeItem = <T>(items: T[], index: number): T[] =>
  items.filter((_, itemIndex) => itemIndex !== index);
